import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  FlaskConical, 
  AlertTriangle, 
  RotateCw, 
  Zap, 
  Terminal, 
  MessageSquare,
  ChevronRight,
  ArrowLeft,
  Construction
} from 'lucide-react';

const BetaTerms: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      <div className="absolute top-0 left-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(168,85,247,0.05)_0%,transparent_70%)] -z-10" />
      
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-purple-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-purple-950 tracking-tighter uppercase">
              Beta <span className="italic text-purple-600">Terms</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              Understanding the experimental nature of Creator Studio systems.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-purple-400 pt-2 border-t border-purple-600/10 w-full">
            <span>Current Phase: Public Beta</span>
            <span className="w-1 h-1 rounded-full bg-purple-200" />
            <span>Ref: BT-2026-V1</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8 text-zinc-600 font-medium leading-relaxed">
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-purple-100 shadow-xl shadow-purple-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-purple-600/20">
                <FlaskConical className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-purple-950 uppercase tracking-tight">1. Experimental Simulation Engine</h2>
                <p className="text-sm">
                  Creator Studio utilizes experimental physics and rendering engines. By participating in our Beta program, you acknowledge that:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  {[
                    'Engine crashes and browser tab hangs may occur.',
                    'Simulation state persistence is not guaranteed.',
                    'Physics calculations may vary between versions.',
                    'Features may be added or removed without notice.'
                  ].map((item, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-purple-50/50 rounded-2xl border border-purple-100 items-center">
                      <Zap className="w-4 h-4 text-purple-400 shrink-0" />
                      <p className="text-xs font-bold text-purple-900 leading-tight">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-amber-50/20 p-8 md:p-10 rounded-[2rem] border border-amber-100 shadow-xl shadow-amber-900/5"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start text-zinc-600">
              <div className="w-14 h-14 bg-amber-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-amber-600/20">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4 w-full">
                <h2 className="text-xl font-heavy font-display text-amber-950 uppercase tracking-tight">2. Cloud Rendering & Video Exports</h2>
                <p className="text-sm leading-relaxed">
                  Cloud rendering is an experimental, resource-intensive service. During beta:
                </p>
                <ul className="space-y-3 mt-4">
                    {[
                      'Video exports may fail or time out without a refund of render credits.',
                      'Rendering resolution may be throttled during peak load.',
                      'Exported videos may contain visual artifacts or glitching.',
                      'The render engine may be taken offline for maintenance at any time.'
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-4 text-xs font-bold p-3 bg-white rounded-xl border border-amber-100/50 text-amber-900">
                        <Construction className="w-4 h-4 text-amber-500" />
                        {item}
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-purple-100 shadow-xl shadow-purple-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-purple-50 rounded-2xl flex items-center justify-center shrink-0 border border-purple-100">
                <RotateCw className="w-7 h-7 text-purple-600" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-purple-950 uppercase tracking-tight">3. Data Loss Disclaimer</h2>
                <p className="text-sm">
                  We frequently purge test data and optimize database schemas. Your simulation project files and account metadata are <strong className="text-purple-600">NOT guaranteed to persist</strong> if a major structural update is deployed to the beta environment.
                </p>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-purple-50/20 p-8 md:p-10 rounded-[2rem] border border-purple-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center shrink-0">
                <MessageSquare className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6 w-full">
                <h2 className="text-xl font-heavy font-display text-purple-950 uppercase tracking-tight italic">4. Feedback & Bug Reporting</h2>
                <p className="text-sm">
                  By using Creator Studio Beta, you agree that your feedback and bug reports may be used by Fun Circle Games to improve our commercial software without compensation.
                </p>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-purple-100 shadow-2xl shadow-purple-950/5 relative overflow-hidden font-sans">
              <div className="absolute top-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-[100px] -z-0" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-heavy font-display text-purple-950 uppercase tracking-tight leading-none italic font-black">
                    Beta <br />
                    <span className="text-purple-600/60 transition-all group-hover:text-purple-600">Agreement</span>
                  </h2>
                  <p className="text-zinc-500 font-medium text-xs leading-relaxed max-w-sm">
                    Participation in our beta program is entirely voluntary and conducted at your own risk.
                  </p>
                </div>
                <div className="bg-purple-50 p-8 rounded-[2rem] border border-purple-100">
                  <div className="flex items-center gap-4 mb-4">
                    <Terminal className="w-6 h-6 text-purple-600 opacity-50" />
                    <span className="text-[10px] font-black uppercase tracking-[0.3em] text-purple-400">Environment Mode</span>
                  </div>
                  <p className="text-xs font-bold leading-relaxed uppercase tracking-tighter italic text-purple-950">
                    THIS SOFTWARE IS PROVIDED "AS IS" AND "WITH ALL FAULTS," AND FUN CIRCLE GAMES MAKES NO REPRESENTATIONS OF ANY KIND.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default BetaTerms;
